import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, AlertTriangle, Users, MapPin, Shield, ArrowRight, Building } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const TacomaSlipFallPage = () => {
  const immediateSteps = [
    {
      title: "Seek medical attention right away",
      description: "Even if injuries seem minor—adrenaline masks pain, and delays hurt your claim."
    },
    {
      title: "Report to property owner/manager",
      description: "Report to mall security or property manager. Get an incident report in writing."
    },
    {
      title: "Document the hazard",
      description: "Take photos of cracked sidewalks near downtown, wet entryways, or any dangerous conditions."
    },
    {
      title: "Get witness information",
      description: "Collect names and contact details from anyone who saw the fall."
    },
    {
      title: "Don't give recorded statements",
      description: "Avoid speaking to insurance adjusters without legal advice first."
    },
    {
      title: "Contact me promptly",
      description: "Washington's 3-year statute of limitations means evidence disappears fast. Act now."
    }
  ];

  const commonCauses = [
    "Spills in busy retail/grocery areas like Tacoma Mall",
    "Wet/icy parking lots and walkways",
    "Faulty stairs/handrails in apartments or public buildings",
    "Inadequate lighting in lots or hallways",
    "Debris or uneven pavement in commercial zones"
  ];

  const compensationTypes = [
    "Medical expenses (current and future)",
    "Lost income and earning capacity",
    "Pain and suffering",
    "Rehabilitation and therapy costs",
    "Reduced quality of life"
  ];

  const faqs = [
    {
      question: "Can I recover if I was partially at fault?",
      answer: "Yes. Washington uses pure comparative negligence—your award is reduced by your percentage of fault, but you can still recover damages."
    },
    {
      question: "What if insurance denies the claim?",
      answer: "We prove negligence with photos, witness statements, maintenance records, and other evidence. Denials aren't the final word."
    }
  ];

  const faqSchemaData = [
    {
      question: "Can I recover compensation if I was partially at fault for my slip and fall in Tacoma?",
      answer: "Yes. Washington uses pure comparative negligence, meaning your compensation is reduced by your percentage of fault, but you can still recover damages even if partially responsible."
    },
    {
      question: "What if the insurance company denies my Tacoma slip and fall claim?",
      answer: "Amy proves negligence using photos, witness statements, maintenance records, and surveillance footage. Insurance denials are not the final word—many denied claims result in fair settlements when properly challenged."
    },
    {
      question: "How long do I have to file a slip and fall lawsuit in Tacoma, Washington?",
      answer: "Washington has a 3-year statute of limitations for personal injury claims (RCW 4.16.080). Contact an attorney promptly to preserve evidence and protect your rights."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Tacoma Slip and Fall Lawyer | Premises Liability Attorney | Amy C. Brown"
        description="Hurt in a slip and fall accident in Tacoma? Amy C. Brown provides honest help and fights for maximum compensation. No fees unless you win. Call (425) 675-1224."
        faqData={faqSchemaData}
        localArea={{ city: "Tacoma", county: "Pierce", state: "Washington" }}
      />

      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-slate-800 via-slate-900 to-secondary overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <MapPin className="w-4 h-4 text-teal-400" />
              <span className="text-white/90 text-sm font-medium">Serving Tacoma & Pierce County</span>
            </div>
            
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Tacoma Slip and Fall Lawyer – Honest Help After Premises Liability Injuries
            </h1>
            
            <p className="text-xl text-slate-300 leading-relaxed mb-6">
              Slip and fall accidents happen often in Tacoma—at the Tacoma Mall, grocery stores in University Place, restaurants on Pacific Avenue, apartment complexes, public sidewalks, parking lots, or workplaces.
            </p>

            <p className="text-lg text-slate-400 leading-relaxed mb-8">
              Rainy South Sound weather, spills, uneven surfaces, poor lighting, or neglected stairs lead to painful injuries: fractures, concussions, herniated discs, and more.
            </p>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-white/10">
              <p className="text-white leading-relaxed">
                I fight for Tacoma injury victims on <strong>contingency—no recovery, no fee</strong>. My prior defense experience helps me dismantle lowball offers and build cases that win.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-semibold transition-all hover:shadow-lg"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-8 py-4 rounded-full font-semibold transition-all border border-white/20"
                data-testid="hero-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Immediate Steps Section */}
      <section className="py-20 bg-white" data-testid="steps-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4 text-center">
              Immediate Steps After a Tacoma Slip and Fall
            </h2>
            <p className="text-lg text-slate-600 text-center mb-12">
              Protect your health and your legal rights from day one.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {immediateSteps.map((step, index) => (
                <div key={index} className="flex gap-4 bg-slate-50 rounded-xl p-6">
                  <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="font-semibold text-secondary mb-1">{step.title}</h3>
                    <p className="text-slate-600 text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Causes Section */}
      <section className="py-20 bg-slate-50" data-testid="causes-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                Common Tacoma Slip and Fall Hazards
              </h2>
              <ul className="space-y-4">
                {commonCauses.map((cause, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">{cause}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="font-heading text-2xl font-bold text-secondary mb-4">
                Compensation You Can Pursue
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Under Washington premises liability law, property owners must maintain safe conditions. If negligence caused your fall, you can seek:
              </p>
              <ul className="space-y-3 mb-6">
                {compensationTypes.map((type, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    <span className="text-slate-700">{type}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-teal-50 rounded-xl p-4 border border-teal-100">
                <p className="text-sm text-slate-600">
                  Same WA premises liability rules apply: <strong>3-year statute of limitations</strong> and <strong>comparative negligence</strong> (you can recover even if partially at fault).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Me Section */}
      <section className="py-20 bg-white" data-testid="why-me-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4 text-center">
              My Edge for Tacoma Clients
            </h2>
            <p className="text-lg text-slate-600 text-center mb-12">
              Big firms pass you around; I handle everything personally.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-50 rounded-xl p-6">
                <Users className="w-10 h-10 text-teal-600 mb-4" />
                <h3 className="font-semibold text-secondary mb-2">Direct Solo Representation</h3>
                <p className="text-slate-600">You work with me—not a paralegal or associate who barely knows your name.</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6">
                <Shield className="w-10 h-10 text-teal-600 mb-4" />
                <h3 className="font-semibold text-secondary mb-2">Aggressive Negotiation</h3>
                <p className="text-slate-600">I know insurance tactics and fight to maximize your settlement.</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6">
                <Building className="w-10 h-10 text-teal-600 mb-4" />
                <h3 className="font-semibold text-secondary mb-2">Defense Background</h3>
                <p className="text-slate-600">Former defense attorney—I know how the other side thinks and operates.</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6">
                <CheckCircle className="w-10 h-10 text-teal-600 mb-4" />
                <h3 className="font-semibold text-secondary mb-2">Real Results</h3>
                <p className="text-slate-600">Proven track record with similar cases throughout the South Sound.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 bg-slate-50" data-testid="faq-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-12 text-center">
              Common Questions
            </h2>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-secondary mb-2">{faq.question}</h3>
                  <p className="text-slate-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Free Guidance for Your Tacoma Slip and Fall
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Call/Text <strong className="text-white">(425) 675-1224</strong> today. No pressure—just answers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-primary text-white hover:bg-primary-hover px-8 py-4 rounded-full font-semibold transition-all hover:shadow-lg"
                data-testid="cta-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-semibold transition-all"
                data-testid="cta-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 bg-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/faqs" 
              className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all"
            >
              See Full FAQs <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all"
            >
              Read Blog Posts <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/is-amy-right-for-you" 
              className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all"
            >
              Is Amy Right For You? <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TacomaSlipFallPage;
